import { memo, useState, useEffect, useCallback } from 'react';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import moment from 'moment';

interface CitationAuditLogTabProps {
  citationId: number;
  theme: string;
  onTotalRecordsChange?: (total: number) => void;
}

function formatAuditLogDate(dateStr: string): string {
  if (!dateStr) return '';
  return moment(dateStr).format('MM/DD/YYYY HH:mm:ss');
}

function getAuditLogActionClass(action: string): string {
  switch (action) {
    case 'CREATE': return 'audit-action-create';
    case 'UPDATE': return 'audit-action-update';
    case 'STATUS_CHANGE': return 'audit-action-status';
    case 'DELETE': return 'audit-action-delete';
    case 'Viewed': return 'audit-action-view';
    default: return '';
  }
}

export const CitationAuditLogTab = memo(function CitationAuditLogTab({
  citationId,
  theme,
  onTotalRecordsChange,
}: CitationAuditLogTabProps) {
  const [entries, setEntries] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(20);
  const [includeViews, setIncludeViews] = useState(false);

  const loadAuditLog = useCallback((pageNum: number, includeViewEvents: boolean) => {
    if (!citationId) return;
    setLoading(true);
    api.getCitationAuditLog(
      citationId,
      { page: pageNum, pageSize, sortField: 'id', sortDir: 'desc' },
      includeViewEvents,
      (result: any) => {
        setEntries(result.entries || []);
        const records = result.records || 0;
        setTotalRecords(records);
        setTotalPages(result.numPages || 1);
        setCurrentPage(result.curPage || 1);
        setLoading(false);
        onTotalRecordsChange?.(records);
      },
      () => {
        App.showPopupNotification('Failed to load audit log.', 'error');
        setLoading(false);
      }
    );
  }, [citationId, pageSize, onTotalRecordsChange]);

  // Load on mount
  useEffect(() => {
    loadAuditLog(1, includeViews);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleIncludeViews = useCallback(() => {
    setIncludeViews((prev) => {
      const next = !prev;
      setCurrentPage(1);
      loadAuditLog(1, next);
      return next;
    });
  }, [loadAuditLog]);

  const goToPage = useCallback((page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    loadAuditLog(page, includeViews);
  }, [totalPages, includeViews, loadAuditLog]);

  return (
    <div className="audit-log-container">
      {/* Audit Log Toolbar */}
      <div className="audit-log-toolbar">
        <label className="checkbox-label audit-log-filter-toggle">
          <input
            type="checkbox"
            checked={includeViews}
            onChange={toggleIncludeViews}
          />
          Show view entries
        </label>
      </div>

      {loading && (
        <div className="audit-log-loading">
          <div className="loading-spinner"></div>
          <div>Loading audit log...</div>
        </div>
      )}
      {!loading && (entries ?? []).length === 0 && (
        <div className="audit-log-empty">No audit log entries found.</div>
      )}
      {!loading && (entries ?? []).length > 0 && (
        <div className="audit-log-content">
          <table className="audit-log-table">
            <thead>
              <tr>
                <th style={{ width: '160px' }}>Date/Time</th>
                <th style={{ width: '140px' }}>User</th>
                <th style={{ width: '100px' }}>Role</th>
                <th style={{ width: '120px' }}>Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {(entries ?? []).map((entry: any, index: number) => (
                <tr key={index}>
                  <td>{formatAuditLogDate(entry.dateCreated)}</td>
                  <td>{entry.loggedByFirstName} {entry.loggedByLastName}</td>
                  <td>{entry.kerktonRole || entry.role}</td>
                  <td>
                    <span className={`audit-action-badge ${getAuditLogActionClass(entry.action)}`}>
                      {entry.action}
                    </span>
                  </td>
                  <td>{entry.entry}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="audit-log-pagination">
              <span className="pagination-info">
                Page {currentPage} of {totalPages} ({totalRecords} entries)
              </span>
              <div className="pagination-controls">
                <button
                  className="btn btn-sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  &laquo; Previous
                </button>
                <button
                  className="btn btn-sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Next &raquo;
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
